import React, { useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as businessService from '../../data-layer/business/service';
import { showNotificationAsync } from '../../data-layer/notifications/actions';
import { useDispatch } from '../../data-layer/useDispatch';
import { setUserRecords } from '../../data-layer/user/actions';
import { toBusinessRegistrationPersistenceModel } from '../../mappers/business/toBusinessRegistratuionPersistenceModel';
import { BusinessRegistrationStatus } from '../../mappers/users/toUserModel';
import { BusinessRegistrationForm } from '../../ui/business/BusinessRegistrationForm';
import { MessengerButton } from '../../ui/business/MessengerWidget';
import { RegistrationStatus } from '../../ui/business/RegistrationStatus';
import { BusinessRegistrationUI } from '../../ui/business/types';
import { DashboardPage } from '../../ui/dashboard/DashboardPage';
import { IconClock } from '../../ui/icon/IconClock';
import { IconCopy } from '../../ui/icon/IconCopy';
import { IconDeclinedFilled } from '../../ui/icon/IconDeclinedFilled';
import { IconKey } from '../../ui/icon/IconKey';
import { IconPause } from '../../ui/icon/IconPause';
import { Box } from '../../ui/layout/Box';
import { Flex } from '../../ui/layout/Flex';
import { Typography } from '../../ui/typography/Typography';
import { canRegisterBusiness } from '../../utils/accessService';
import { trackFieldFilled, ymTrackChatClick, ymTrackSubmitRegistration } from '../../utils/metrika';
import { share } from '../../utils/share';
import { useTranslation } from '../../utils/useTranslation';
import { useCurrentUser } from '../users/useCurrentUser';
import { withAuthProtection } from '../withAuthProtection';

const MODERATION_RULES_LINK =
    'https://yandex.ru/support2/rythm/ru/buisness/moderation#tematiki,-kotorye-ne-razmeshayutsya-v-yandeks-ritme';

const getRegistrationStatusProps = (
    status?: BusinessRegistrationStatus,
    login?: string,
    onCopy?: () => Promise<void>,
) => {
    const t = useTranslation();

    if (status === BusinessRegistrationStatus.VERIFICATION) {
        return {
            iconSlot: <IconKey size={64} />,
            title: 'Подтверждение доступа',
            messageSlot: (
                <Flex>
                    <Typography pb={0.5}>
                        {t('Необходимо подтвердить ваш доступ к социальной сети бизнеса.')}
                    </Typography>
                    <Typography pb={0.5}>
                        {t(
                            'Для этого нужно написать в сообщения в Direct нашему профилю yandex.rythm от имени логина, с которого вы создали данную заявку.',
                        )}
                    </Typography>
                    <Typography pb={2}>{t('Текст сообщения: ')}</Typography>
                    <Flex
                        p={2}
                        direction="row"
                        backgroundColor="var(--background-secondary)"
                        borderRadius="16px"
                        alignItems="center"
                    >
                        <Typography>
                            {t(`${login} подтверждает доступ к аккаунту бизнеса.`)}
                        </Typography>
                        <IconCopy onClick={onCopy} size={24} cursor="pointer" />
                    </Flex>
                    <Typography pt={2} pb={0.5}>
                        {t(
                            'Мы получим ваше сообщение и добавим вас в список желающих попасть в бету.',
                        )}
                    </Typography>
                    <Typography>
                        {t('Если возникли вопросы, вы всегда можете обратиться в ')}
                        <Typography
                            display="inline"
                            as={Link}
                            to={
                                `${process.env.REACT_APP_BETA_SUPPORT_FORM_LINK}?login=${login}` ||
                                ''
                            }
                            color="link"
                            textDecoration="none"
                        >
                            {t('поддержку')}.
                        </Typography>
                    </Typography>
                </Flex>
            ),
        };
    }
    if (status === BusinessRegistrationStatus.MODERATION) {
        return {
            iconSlot: <IconClock size={64} />,
            title: 'Проверяем аккаунт',
            messageSlot: (
                <Flex>
                    <Typography pb={0.5}>
                        {t('Нам нужно немного времени, мы свяжемся с вами позже.')}
                    </Typography>
                    <Typography>
                        {t('Если возникли вопросы, вы всегда можете обратиться в ')}
                        <Typography
                            display="inline"
                            as={Link}
                            to={
                                `${process.env.REACT_APP_BETA_SUPPORT_FORM_LINK}?login=${login}` ||
                                ''
                            }
                            color="link"
                            textDecoration="none"
                        >
                            {t('поддержку')}.
                        </Typography>
                    </Typography>
                </Flex>
            ),
        };
    }
    if (status === BusinessRegistrationStatus.DECLINED_FOR_BETA) {
        return {
            iconSlot: <IconPause size={64} color="var(--color-danger)" />,
            title: 'Пока не можем вас подключить',
            messageSlot: (
                <Flex>
                    <Typography pb={0.5}>
                        {t(
                            'К сожалению, мы не можем пригласить ваш бизнес в бету. Мы свяжемся с вами, когда этап бета-тестирования завершится, и появится возможность попасть в Ритм.',
                        )}
                    </Typography>
                    <Typography>
                        {t('Если возникли вопросы, вы всегда можете обратиться в ')}
                        <Typography
                            display="inline"
                            as={Link}
                            to={
                                `${process.env.REACT_APP_BETA_SUPPORT_FORM_LINK}?login=${login}` ||
                                ''
                            }
                            color="link"
                            textDecoration="none"
                        >
                            {t('поддержку')}.
                        </Typography>
                    </Typography>
                </Flex>
            ),
        };
    }
};

export const BusinessRegistration = withAuthProtection(() => {
    const t = useTranslation();
    const dispatch = useDispatch();
    const currentUser = useCurrentUser();
    const navigate = useNavigate();

    const onSubmit = useCallback(
        async (businessRegistrationUI: BusinessRegistrationUI) => {
            try {
                await businessService.registerBusiness(
                    toBusinessRegistrationPersistenceModel(businessRegistrationUI),
                );

                if (currentUser) {
                    dispatch(
                        setUserRecords({
                            ...currentUser,
                            businessRegistrationStatus: BusinessRegistrationStatus.VERIFICATION,
                        }),
                    );
                }
                ymTrackSubmitRegistration({
                    userLogin: currentUser?.login,
                    page: 'Регистрации бизнеса',
                });
                dispatch(showNotificationAsync(t('Заявка на участие в бете подана')));
            } catch (error) {
                dispatch(showNotificationAsync(t('Не удалось подать заявку на участие в бете')));
            }
        },
        [currentUser, dispatch],
    );

    useEffect(() => {
        if (currentUser?.businessRegistrationStatus === BusinessRegistrationStatus.REGISTERED) {
            navigate(currentUser.profile?.url || '/');

            return;
        }
    }, [currentUser?.businessRegistrationStatus, currentUser?.profile?.url]);

    const onCopy = useCallback(async () => {
        await share({
            text: `${currentUser?.login} подтверждает доступ к аккаунту бизнеса.`,
        });
        dispatch(showNotificationAsync(t('Ссылка успешно скопирована')));
    }, [currentUser?.login, dispatch]);

    const onFieldBlur = useCallback(
        (fieldName: string, value: string) => {
            if (value) {
                trackFieldFilled(fieldName, {
                    userLogin: currentUser?.login,
                    page: 'Регистрации бизнеса',
                    value,
                });
            }
        },
        [currentUser?.login],
    );

    const onChatClick = useCallback(() => {
        ymTrackChatClick({
            page: 'Регистрация',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    return (
        <DashboardPage>
            <Box position="fixed" right={2} bottom={{ xs: 8, sm: 2 }}>
                <MessengerButton onClick={onChatClick} />
            </Box>
            {currentUser ? (
                canRegisterBusiness(currentUser.businessRegistrationStatus) ? (
                    <Flex>
                        {currentUser.businessRegistrationStatus ===
                            BusinessRegistrationStatus.DECLINED && (
                            <Flex
                                direction="row"
                                alignItems="center"
                                spacing={2}
                                p={2}
                                borderRadius={2}
                                backgroundColor="var(--background-primary)"
                                mb={1}
                            >
                                <IconDeclinedFilled size={24} color="var(--color-danger)" />
                                <Flex>
                                    <Typography pb={0.5}>
                                        {t(
                                            'К сожалению, не сможем принять ваш бизнес в Яндекс Ритм. Это связано с категорией товаров и услуг вашего бизнеса — подробнее рассказали в ',
                                        )}
                                        <Typography
                                            display="inline"
                                            as={Link}
                                            to={MODERATION_RULES_LINK}
                                            color="link"
                                            textDecoration="none"
                                        >
                                            {t('Справке')}
                                        </Typography>
                                        .
                                    </Typography>
                                    <Typography>
                                        {t('Если возникли вопросы, вы всегда можете обратиться в ')}
                                        <Typography
                                            display="inline"
                                            as={Link}
                                            to={
                                                `${process.env.REACT_APP_BETA_SUPPORT_FORM_LINK}?login=${currentUser.login}` ||
                                                ''
                                            }
                                            color="link"
                                            textDecoration="none"
                                        >
                                            {t('поддержку')}.
                                        </Typography>
                                    </Typography>
                                </Flex>
                            </Flex>
                        )}

                        <BusinessRegistrationForm
                            initialValues={{
                                domain: '',
                                instagramUrl: '',
                                telegramUrl: '',
                                displayName: '',
                                offerAgreement: false,
                                sendAdMails: true,
                                email: `${currentUser.login}@yandex.ru`,
                            }}
                            onSubmit={onSubmit}
                            onFieldBlur={onFieldBlur}
                        />
                    </Flex>
                ) : (
                    <RegistrationStatus
                        {...getRegistrationStatusProps(
                            currentUser.businessRegistrationStatus,
                            currentUser.login,
                            onCopy,
                        )}
                    />
                )
            ) : (
                <></>
            )}
        </DashboardPage>
    );
});
